import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'
import { OrganizationSlugsDocument } from '@/graphql/generated'
import { graphqlRequest } from '@/lib/api/graphql'
import { fetchSession } from '@/lib/queries/session'
import { nodes } from '@/lib/queries/mappers'
import { currentUser } from '@/lib/auth/session'
import type { LegacyRedirect } from '@/lib/legacy-routes'

/**
 * A splat param (`$`) as the path segments the resolvers expect.
 *
 * `_splat` is undefined for a bare `/organizations` and a single joined string
 * otherwise. Empty segments are dropped so a trailing slash — which these old
 * URLs collected freely, being pasted into mail clients and spreadsheets — does
 * not arrive as an empty segment and shift by position everything the resolver
 * reads.
 */
export function splitSplat(splat: string | undefined): string[] {
  return (splat ?? '').split('/').filter(Boolean)
}

/**
 * Turn a resolved legacy URL into an actual redirect.
 *
 * The uuid → slug lookup goes through the same policy-scoped GraphQL path as
 * everything else, so a link to an organization the caller cannot see resolves
 * to nothing rather than leaking that it exists.
 *
 * A miss is NOT a 404. These URLs are years old: the organization may have been
 * renamed, merged or left, and dead-ending someone on a link that worked last
 * week — when their own workspace is one lookup away — is the worse answer.
 * They land in their own workspace instead, at the equivalent screen.
 *
 * This used to run on the server, one hop from the API. It now costs the
 * browser a round trip before the redirect resolves, which is the honest price
 * of deleting the server — and it is paid only on URLs the old app minted, by
 * someone following a link they saved years ago.
 */
export async function redirectFromLegacyPath(
  queryClient: QueryClient,
  resolution: LegacyRedirect,
): Promise<never> {
  const user = await currentUser()
  /* The root gate covers these routes, so this is belt-and-braces — but a
     screen that assumes a session and gets none renders an error, and the
     honest answer to "who are you" being unknown is the login screen. */
  if (!user) throw redirect({ to: '/login' })

  const session = await fetchSession(queryClient, user.id)

  const fallbackSlug = session.organizations[0]?.slug

  if (resolution.organizationId) {
    const data = await graphqlRequest(OrganizationSlugsDocument, {
      ids: [resolution.organizationId],
    })
    const slug = nodes(data.organizationsCollection)[0]?.slug

    if (slug) throw redirect({ ...resolution.build(slug) })
  }

  if (!fallbackSlug) {
    /* Same reasoning as the root route: sign-up always creates a personal
       organization, so an account with none is broken rather than new. */
    throw new Error('This account does not belong to any organization.')
  }

  throw redirect({ ...resolution.build(fallbackSlug) })
}
